import type {
  Automation,
  AUTOMATION_TRIGGER_COMBINATION,
  AUTOMATION_ACTION_TRIGGER_TYPE,
} from '../../MedicareProAPI/Entities/Automation/Automation';
import type { AUTOMATION_ACTION_TYPE } from '../../MedicareProAPI/Entities/Automation/AutomationAction';
import { AutomationAction } from '../../MedicareProAPI/Entities/Automation/AutomationAction';
import { AutomationActionRepository } from '../../MedicareProAPI/Repositories/Automation/AutomationActionRepository';
import { AutomationRepository } from '../../MedicareProAPI/Repositories/Automation/AutomationRepository';
import { GlobUserTemplateRepository } from '../../MedicareProAPI/Repositories/Glob/GlobUserTemplateRepository';
import { UserIntegrationOAuthVoipResourceRepository } from '../../MedicareProAPI/Repositories/User/UserIntegrationOAuthVoipResourceRepository';
import { UserLetterRepository } from '../../MedicareProAPI/Repositories/User/UserLetterRepository';

export interface AutomationSpec {
  name: string;
  isActive: boolean;
  triggerType: AUTOMATION_ACTION_TRIGGER_TYPE;
  triggerEntity: string;
  triggerData: any;
  recipe: AUTOMATION_TRIGGER_COMBINATION;
}

export interface AutomationActionSpec {
  type: AUTOMATION_ACTION_TYPE;
  position: number;
  data: Record<string, any>;
  startDelayDays: number | null;
  startDelayTime: string | null;
  templateId?: number | string;
}

export interface AutomationTemplate {
  metaData: {
    slug: string;
    description: string;
    category: string;
  };
  automation: AutomationSpec;
  automationActions: AutomationActionSpec[];
  messageTemplates: Array<number | string>;
}

export const AutomationTemplateBlueprints = [
  {
    metaData: {
      slug: '',
      description:
        'This automation kicks in whenever a new policy with status Pending is added to a client’s profile. It immediately sends a clear “your enrollment is being processed” message. Customize the content or swap the channel (SMS, task, etc.). If you’ve connected SunFire or Connecture, incoming enrollment data will also trigger this automation automatically. It’s a simple way to reassure clients and reduce “just checking” calls.',
      vendorRequirements: ['email'],
      category: 'Retention',
      templatesCreated: ['New Policy'],
    },
    automation: {
      name: 'New policy',
      isActive: false,
      triggerType: 'insert',
      triggerEntity: 'IndPolicy',
      triggerData: [
        {
          field: 'policyStatus',
          values: ['Pending'],
          required: true,
        },
      ],
      recipe: 'ind_policy_status_insert',
    },
    automationActions: [
      {
        type: 'send_email',
        position: 0,
        data: {},
        startDelayDays: null,
        startDelayTime: null,
        templateId: 24,
      },
    ],
    messageTemplates: [24],
  },
  {
    metaData: {
      slug: '',
      description:
        'Trigger this workflow when a client’s policy becomes Active. They receive a same-day confirmation with key policy info and next steps. The sequence continues with friendly check-ins at 1, 3, 6, and 9 months, making it easy to nurture clients across their first year. Customize subject lines and content with merge tags for a personal touch.',
      vendorRequirements: ['email'],
      category: 'Retention',
      templatesCreated: [
        'Your Policy is now active!',
        'New Policy - 1 Month Followup',
        'New Policy - 3 Month Followup',
        'New Policy - 6 Month Followup',
        'New Policy - 9 Month Followup',
      ],
    },
    automation: {
      name: 'Policy Issued',
      isActive: false,
      triggerType: 'update',
      triggerEntity: 'IndPolicy',
      triggerData: [
        {
          field: 'policyStatus',
          values: ['Active'],
          required: true,
        },
      ],
      recipe: 'ind_policy_status_update',
    },
    automationActions: [
      {
        type: 'send_email',
        position: 0,
        data: {},
        startDelayDays: null,
        startDelayTime: null,
        templateId: 23,
      },
      {
        type: 'send_email',
        position: 1,
        data: {},
        startDelayDays: 30,
        startDelayTime: null,
        templateId: 43,
      },
      {
        type: 'send_email',
        position: 2,
        data: {},
        startDelayDays: 90,
        startDelayTime: null,
        templateId: 44,
      },
      {
        type: 'send_email',
        position: 3,
        data: {},
        startDelayDays: 180,
        startDelayTime: null,
        templateId: 45,
      },
      {
        type: 'send_email',
        position: 4,
        data: {},
        startDelayDays: 270,
        startDelayTime: null,
        templateId: 46,
      },
    ],
    messageTemplates: [23, 43, 44, 45, 46],
  },
  {
    metaData: {
      slug: '',
      description:
        'When an Active Client has a birthday, this workflow sends a friendly email with a birthday card image. A simple, thoughtful touch that boosts goodwill and replies. You can pair this with an SMS message as well. Just add another action and you can make an SMS template on the fly.',
      vendorRequirements: ['email'],
      category: 'Retention',
      templatesCreated: ['Happy Birthday Calm'],
    },
    automation: {
      name: 'Happy Birthday',
      isActive: false,
      triggerType: 'birthday',
      triggerEntity: 'Ind',
      triggerData: [
        {
          field: 'indStatus',
          values: ['Active Client'],
        },
      ],
      recipe: 'ind_birthday',
    },
    automationActions: [
      {
        type: 'send_email',
        position: 0,
        data: {},
        startDelayDays: null,
        startDelayTime: null,
        templateId: 8,
      },
    ],
    messageTemplates: [8],
  },
  {
    metaData: {
      slug: '',
      description:
        'This template triggers on Lead Status = Called and immediately sends a personalized thank-you email. Use it as a foundation for a gentle drip: schedule a next-day follow-up, create a callback task, or add an SMS nudge if there’s no reply. Perfect for honing your automation skills—layer criteria, try different channels, and iterate toward stronger engagement.',
      vendorRequirements: ['email'],
      category: 'Lead',
      templatesCreated: ['Great Speaking With You'],
    },
    automation: {
      name: 'Lead Followup',
      isActive: false,
      triggerType: 'update',
      triggerEntity: 'UserLead',
      triggerData: [
        {
          field: 'leadStatus',
          values: ['Called'],
          required: true,
        },
      ],
      recipe: 'user_lead_status_update',
    },
    automationActions: [
      {
        type: 'send_email',
        position: 0,
        data: {},
        startDelayDays: null,
        startDelayTime: null,
        templateId: 41,
      },
    ],
    messageTemplates: [41],
  },
  {
    metaData: {
      slug: '',
      description:
        'Workflow triggers when a 64 year old prospect turns 64 and a half years old. They receive a same-day email encouraging them to schedule a time with you to start talking about Medicare options. The sequence continues with friendly check-ins at 3 and 1 month until 65. Customize subject lines and content with merge tags for a personal touch.',
      vendorRequirements: ['email'],
      category: 'Aging in',
      templatesCreated: [
        'Turning 65 in 6 months',
        'Turning 65 in 3 months',
        'Turning 65 in 2 months',
      ],
    },
    automation: {
      name: 'Aging in',
      isActive: false,
      triggerType: 'birthday_upcoming',
      triggerEntity: 'Ind',
      triggerData: [
        {
          field: 'birthdayUpcoming',
          values: [6],
          required: true,
        },
        {
          field: 'indAge',
          values: 64,
        },
      ],
      recipe: 'ind_birthday_upcoming',
    },
    automationActions: [
      {
        type: 'send_email',
        position: 0,
        data: {},
        startDelayDays: null,
        startDelayTime: null,
        templateId: 47,
        triggerCriteria: [
          {
            field: 'indStatus',
            values: ['Prospect'],
          },
        ],
      },
      {
        type: 'send_email',
        position: 1,
        data: {},
        startDelayDays: 90,
        startDelayTime: null,
        templateId: 49,
        triggerCriteria: [
          {
            field: 'indStatus',
            values: ['Prospect'],
          },
        ],
      },
      {
        type: 'send_email',
        position: 2,
        data: {},
        startDelayDays: 150,
        startDelayTime: null,
        templateId: 51,
        triggerCriteria: [
          {
            field: 'indStatus',
            values: ['Prospect'],
          },
        ],
      },
    ],
    messageTemplates: [47, 49, 51],
  },
];

export async function makeAutomationFromTemplate(
  index,
  userId,
  userAccessToken
): Promise<Automation> {
  const globUserTemplateRepository = new GlobUserTemplateRepository();
  const automationRepository = new AutomationRepository();
  const automationActionRepository = new AutomationActionRepository();
  const userLetterRepository = new UserLetterRepository();
  const userIntegrationOAuthVoipResourceRepository =
    new UserIntegrationOAuthVoipResourceRepository();

  globUserTemplateRepository.setAccessToken(userAccessToken);
  automationRepository.setAccessToken(userAccessToken);
  automationActionRepository.setAccessToken(userAccessToken);
  userLetterRepository.setAccessToken(userAccessToken);
  userIntegrationOAuthVoipResourceRepository.setAccessToken(userAccessToken);

  const blueprints = AutomationTemplateBlueprints;

  const blueprint = blueprints[index];

  if (!blueprint) throw new Error('Template not found');

  const templateAutomation = blueprint.automation;
  const templateActions = blueprint.automationActions;

  const messageTemplates = await globUserTemplateRepository.getGlobUserTemplates();

  const filteredMessagesTemplates = messageTemplates.getItems().filter((item) => {
    return blueprint.messageTemplates.includes(item.getId());
  });

  // 1) Create the automation
  const automationPayload = {
    name: templateAutomation.name,
    isActive: false,
    triggerType: templateAutomation.triggerType,
    triggerEntity: templateAutomation.triggerEntity,
    triggerData: templateAutomation.triggerData,
    recipe: templateAutomation.recipe,
  };
  const newAutomation = await automationRepository.createAutomation(userId, automationPayload);
  const automationId = newAutomation.getId();

  // 2) Index templates for O(1) lookup + validate all are present
  const msgById = new Map(filteredMessagesTemplates.map((t) => [t.getId(), t]));
  const missing = templateActions
    .filter((a) => a.templateId && !msgById.has(a.templateId))
    .map((a) => a.templateId);
  if (missing.length) {
    await automationRepository.deleteAutomation(userId, automationId).catch(() => {});
    throw new Error(`Missing message templates: ${missing.join(', ')}`);
  }

  // 3) Create actions concurrently (and letters if needed)
  const createdLetterIds: number[] = [];

  try {
    await Promise.all(
      templateActions.map(async (item, idx) => {
        let userLetterTemplateId: number | undefined;

        if (item.templateId) {
          const mt = msgById.get(item.templateId)!;
          const letterPayload = {
            letterType: mt.getTemplateType(),
            letterTitle: mt.getTemplateTitle(),
            letterBody: mt.getTemplateBody(), // note the ()
            folder: mt.getCategory(),
          };
          const created = await userLetterRepository.createUserLetter(userId, letterPayload);
          userLetterTemplateId = created;
          createdLetterIds.push(userLetterTemplateId);
        }

        const actionPayload = {
          type: item.type,
          position: item.position ?? idx, // preserve intended order
          data: { ...(item.data ?? {}), ...(userLetterTemplateId ? { userLetterTemplateId } : {}) },
          startDelayDays: item.startDelayDays ?? null,
          startDelayTime: item.startDelayTime ?? null,
          triggerCriteria: item.triggerCriteria ?? null,
        };

        await automationActionRepository.createAutomationAction(
          userId,
          automationId,
          actionPayload
        );
      })
    );

    const thisNewAutomation = automationRepository.getAutomation(userId, newAutomation.getId());
    return thisNewAutomation;
  } catch (err) {
    // Rollback best-effort
    await Promise.all([
      automationRepository.deleteAutomation(userId, automationId).catch(() => {}),
      ...createdLetterIds.map((id) =>
        userLetterRepository.deleteUserLetter?.(userId, id).catch(() => {})
      ),
    ]);
    throw err;
  }
}
